// socketService.js
// Centralized Socket.IO handling for Sampling Event realtime sync

import { io } from "socket.io-client";

let socket = null;
let updateHandler = null; // function(patch) => void - for incoming broadcasts
let jsaUpdateHandler = null; // function(patch) => void - for incoming JSA broadcasts
let connectHandler = null; // function(connected:boolean) => void
let joinAckHandler = null; // function(ack) => void
let visibilityHandler = null; // prevents Heroku H15 idle connection errors on iPad

// Prefer explicit socket URL, then fallback to development backend or current origin
const BACKEND_URL =
  process.env.REACT_APP_SOCKET_URL ||
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === "development"
    ? "http://localhost:5000"
    : window.location.origin);

export const initializeSamplingEventSocket = () => {
  if (socket) return socket;
  socket = io(BACKEND_URL, {
    withCredentials: true,
    transports: ["websocket", "polling"],
    path: "/socket.io",
    reconnectionAttempts: 10,
    reconnectionDelay: 5000,
    reconnectionDelayMax: 30000,
    randomizationFactor: 0.5,
  });

  socket.on("connect", () => {
    if (typeof connectHandler === "function") connectHandler(true);
  });
  socket.on("disconnect", () => {
    if (typeof connectHandler === "function") connectHandler(false);
  });
  // Listen for incoming broadcasts from server
  socket.on("sampling_event_update", (patchData) => {
    if (typeof updateHandler === "function") updateHandler(patchData);
  });
  // Listen for JSA-specific broadcasts (field-level updates)
  socket.on("sampling_event_jsa_update", (patchData) => {
    if (typeof jsaUpdateHandler === "function") jsaUpdateHandler(patchData);
  });

  visibilityHandler = () => {
    if (!socket) return;
    if (document.hidden) {
      socket.disconnect();
    } else {
      socket.connect();
    }
  };
  document.addEventListener("visibilitychange", visibilityHandler);

  return socket;
};

// Register handler for incoming broadcasts (routes to store's applyWebsocketPatch)
export const onSamplingEventUpdate = (fn) => {
  updateHandler = typeof fn === "function" ? fn : null;
};

// Register handler for incoming JSA broadcasts (field-level updates)
export const onSamplingEventJsaUpdate = (fn) => {
  jsaUpdateHandler = typeof fn === "function" ? fn : null;
};

// Register handler for connection status changes
export const onSamplingEventConnectionChange = (fn) => {
  connectHandler = typeof fn === "function" ? fn : null;
  if (connectHandler && socket) {
    try {
      connectHandler(Boolean(socket.connected));
    } catch {}
  }
};

// Optional handler to consume join ACK payloads (e.g., to hydrate event)
export const onSamplingEventJoinAck = (fn) => {
  joinAckHandler = typeof fn === "function" ? fn : null;
};

// Clear all consumer handlers to avoid dangling callbacks after unmount
export const clearSamplingEventHandlers = () => {
  updateHandler = null;
  jsaUpdateHandler = null;
  connectHandler = null;
  joinAckHandler = null;
};

// Check if socket is connected
export const isSamplingEventSocketConnected = () => {
  return Boolean(socket && socket.connected);
};

// Join a sampling event room
export const joinSamplingEvent = (eventId) =>
  new Promise((resolve) => {
    if (!socket) initializeSamplingEventSocket();
    if (!socket)
      return resolve({ success: false, error: "socket_init_failed" });
    socket.emit(
      "join_sampling_event",
      { sampling_event_id: eventId },
      (ack) => {
        try {
          if (typeof joinAckHandler === "function") joinAckHandler(ack);
        } catch {}
        resolve(ack || { success: false });
      }
    );
  });

// Leave a sampling event room
export const leaveSamplingEvent = (eventId) =>
  new Promise((resolve) => {
    if (!socket) return resolve({ success: true });
    socket.emit("leave_sampling_event", { sampling_event_id: eventId }, (ack) =>
      resolve(ack || { success: true })
    );
  });

/**
 * Emit a patch to the server via websocket.
 * Returns { sent: boolean, shouldQueue: boolean, error: string|null }
 * - sent: true if the emit was attempted
 * - shouldQueue: true if the patch should be queued for retry (network issues)
 * - error: error message if failed, null on success
 */
export const emitSamplingEventPatch = (eventId, patch) =>
  new Promise((resolve) => {
    if (!socket || !socket.connected) {
      // No connection - should queue for retry
      return resolve({ sent: false, shouldQueue: true, error: null });
    }

    const payload = { sampling_event_id: eventId, ...patch };

    // Add timeout for no response (network issues)
    const timeout = setTimeout(() => {
      resolve({ sent: true, shouldQueue: true, error: "timeout" });
    }, 10000); // 10 second timeout

    socket.emit("autosave_sampling_event", payload, (ack) => {
      clearTimeout(timeout);
      if (!ack) {
        // No ack at all - network issue, should queue
        resolve({ sent: true, shouldQueue: true, error: "no_response" });
      } else if (ack.success) {
        // Success - don't queue
        resolve({ sent: true, shouldQueue: false, error: null });
      } else {
        // Server responded with error (validation, etc.) - DON'T queue
        resolve({ sent: true, shouldQueue: false, error: ack.error });
      }
    });
  });

// Disconnect socket and clean up
export const disconnectSamplingEventSocket = () => {
  if (socket) {
    try {
      socket.off("sampling_event_update");
      socket.off("sampling_event_jsa_update");
      socket.off("connect");
      socket.off("disconnect");
    } catch {}
    if (visibilityHandler) {
      document.removeEventListener("visibilitychange", visibilityHandler);
      visibilityHandler = null;
    }
    clearSamplingEventHandlers();
    socket.disconnect();
    socket = null;
  }
};
